import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type PropsWithChildren } from 'react';
import { useSound } from '../sound/SoundProvider';
import { GuideCharacter } from './GuideCharacter';
import { REACTION_CATALOG, REACTION_EVENT_IDS, type ReactionDefinition, type ReactionEventId } from './reactionCatalog';

interface ActiveReaction {
  id: ReactionEventId;
  definition: ReactionDefinition;
  message?: string;
  serial: number;
}

interface ReactionContextValue {
  emit: (id: ReactionEventId, options?: { message?: string; silent?: boolean }) => Promise<boolean>;
  active: ActiveReaction | null;
}

const ReactionContext = createContext<ReactionContextValue | null>(null);

function reactionFromTarget(target: EventTarget | null): ReactionEventId | undefined {
  if (!(target instanceof Element)) return undefined;
  const interactive = target.closest<HTMLElement>('[data-reaction]');
  const value = interactive?.dataset.reaction;
  return value && REACTION_EVENT_IDS.includes(value as ReactionEventId) ? value as ReactionEventId : undefined;
}

export function ReactionProvider({ children }: PropsWithChildren) {
  const { play, preferences } = useSound();
  const [active, setActive] = useState<ActiveReaction | null>(null);
  const serialRef = useRef(0);
  const hideTimerRef = useRef<number | null>(null);
  const lastEventRef = useRef<{ id: ReactionEventId; at: number } | null>(null);

  const emit = useCallback(async (id: ReactionEventId, options?: { message?: string; silent?: boolean }) => {
    const definition = REACTION_CATALOG[id];
    const now = Date.now();
    if (lastEventRef.current?.id === id && now - lastEventRef.current.at < 180) return false;
    lastEventRef.current = { id, at: now };

    if (!options?.silent) await play(definition.sound);
    if (preferences.hapticsEnabled && typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      try { navigator.vibrate(definition.haptic); } catch { /* vibration may be blocked */ }
    }

    if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current);
    serialRef.current += 1;
    const next = { id, definition, message: options?.message, serial: serialRef.current };
    setActive(next);
    hideTimerRef.current = window.setTimeout(() => setActive((current) => current?.serial === next.serial ? null : current), definition.durationMs);
    return true;
  }, [play, preferences.hapticsEnabled]);

  useEffect(() => {
    const onPointer = (event: PointerEvent) => {
      const id = reactionFromTarget(event.target);
      if (id) void emit(id);
    };
    const onKeyboard = (event: KeyboardEvent) => {
      if (event.repeat || !['Enter', ' '].includes(event.key)) return;
      const id = reactionFromTarget(event.target);
      if (id) void emit(id);
    };
    document.addEventListener('pointerup', onPointer, true);
    document.addEventListener('keydown', onKeyboard, true);
    return () => {
      document.removeEventListener('pointerup', onPointer, true);
      document.removeEventListener('keydown', onKeyboard, true);
      if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current);
    };
  }, [emit]);

  const value = useMemo(() => ({ emit, active }), [active, emit]);
  return <ReactionContext.Provider value={value}>
    {children}
    <ReactionOverlay active={active} reducedMotion={preferences.reducedMotion} />
  </ReactionContext.Provider>;
}

function ReactionOverlay({ active, reducedMotion }: { active: ActiveReaction | null; reducedMotion: boolean }) {
  if (!active || active.definition.intensity === 'micro') return null;
  const { definition } = active;
  return <aside key={active.serial} className={`reaction-overlay reaction-${definition.intensity} accent-${definition.accent} ${reducedMotion ? 'reduced' : ''}`} role="status" aria-live="polite" aria-atomic="true">
    <span className="reaction-particles" aria-hidden="true"><i/><i/><i/><i/><i/></span>
    <GuideCharacter guide={definition.guide} pose={definition.pose} alt="" state={definition.intensity === 'celebration' ? 'celebrate' : 'guide'} />
    <span className="reaction-copy"><small>{definition.eyebrow}</small><strong>{active.message ?? definition.title}</strong></span>
    <span className="sr-only">{definition.liveMessage}</span>
  </aside>;
}

export function useReaction(): ReactionContextValue {
  const context = useContext(ReactionContext);
  if (!context) throw new Error('useReaction must be used inside ReactionProvider');
  return context;
}
